import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../../utils/api";
import { ROUTES } from "../../../utils/routes";
import { Alert } from "../../../utils/Alert";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Editor from "react-simple-wysiwyg";

export default function InfluencerSubscriptionTypeForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        type: "",
        description: "",
        basePrice: 0,
        offerPercentage: 0,
        price: 0,
        duration_days: 30,
        paid_quota: 0,
        barter_quota: 0,
        affiliate_quota: 0,
        performance_quota: 0,
        isActive: true
    });

    useEffect(() => {
        if (isEdit) {
            fetchDetails();
        }
    }, [id]);

    useEffect(() => {
        // Auto-calculate price
        const base = parseFloat(formData.basePrice) || 0;
        const offer = parseFloat(formData.offerPercentage) || 0;
        const calculated = base - (base * offer / 100);
        setFormData(prev => ({ ...prev, price: calculated >= 0 ? calculated : 0 }));
    }, [formData.basePrice, formData.offerPercentage]);

    const fetchDetails = () => {
        api.getApiCall(`influencer-subscription/type/details/${id}`, "", (response) => {
            if (response.data && response.data.result) {
                const data = response.data.result;
                setFormData({
                    type: data.type || "",
                    description: data.description || "",
                    basePrice: data.basePrice || 0,
                    offerPercentage: data.offerPercentage || 0,
                    price: data.price || 0,
                    duration_days: data.duration_days || 30,
                    paid_quota: data.paid_quota || 0,
                    barter_quota: data.barter_quota || 0,
                    affiliate_quota: data.affiliate_quota || 0,
                    performance_quota: data.performance_quota || 0,
                    isActive: !!data.isActive
                });
            }
        }, (error) => {
            console.error("Failed to fetch details", error);
        });
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { ...formData };
        if (isEdit) {
            api.patchApiCall(`influencer-subscription/type/update/${id}`, payload, (response) => {
                Alert(1, "Type updated successfully");
                navigate(ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_LIST);
            }, (error) => {
                console.error("Update failed", error);
                Alert(2, "Update failed");
            });
        } else {
            api.postApiCall("influencer-subscription/type/create", payload, (response) => {
                Alert(1, "Type created successfully");
                navigate(ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_LIST);
            }, (error) => {
                console.error("Create failed", error);
                Alert(2, "Create failed");
            });
        }
    };

    return (
        <Box p={3}>
            <Stack direction="row" alignItems="center" gap={2} mb={3}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_LIST)}>
                    Back
                </Button>
                <Typography variant="h5">{isEdit ? "Edit Subscription Type" : "Add Subscription Type"}</Typography>
            </Stack>

            <Card>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Type Name"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Duration (Days)"
                                    name="duration_days"
                                    type="number"
                                    value={formData.duration_days}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Base Price"
                                    name="basePrice"
                                    type="number"
                                    value={formData.basePrice}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Offer Percentage (%)"
                                    name="offerPercentage"
                                    type="number"
                                    value={formData.offerPercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Calculated Price"
                                    name="price"
                                    type="number"
                                    value={formData.price}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>Quotas</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Paid Quota"
                                    name="paid_quota"
                                    type="number"
                                    value={formData.paid_quota}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Barter Quota"
                                    name="barter_quota"
                                    type="number"
                                    value={formData.barter_quota}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Affiliate Quota"
                                    name="affiliate_quota"
                                    type="number"
                                    value={formData.affiliate_quota}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Performance Quota"
                                    name="performance_quota"
                                    type="number"
                                    value={formData.performance_quota}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                        />
                                    }
                                    label="Is Active"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>Description</Typography>
                                <Editor
                                    value={formData.description}
                                    onChange={(e) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            description: e.target.value
                                        }));
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button variant="outlined" onClick={() => navigate(ROUTES.INFLUENCER_SUBSCRIPTION_TYPE_LIST)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary">
                                        {isEdit ? "Update" : "Create"}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
}
